/*
 * 시설물 예약 페이지 (FacilityReservationPage)
 * 
 * ⚠️  임시 접근 제한 적용됨 ⚠️
 * 
 * 현재 이 페이지는 순차 오픈을 위해 특정 권한의 사용자만 접근할 수 있도록 제한되어 있습니다.
 * 
 * 제한 조건:
 * - userLevel 4 이하: 접근 허용 (공사 관계자, 센터 관계자, 시스템 관리자)
 * - userLevel 7: 추가로 접근 허용
 * - userLevel 5, 6, 8 이상: 접근 제한 → /site-preparation 페이지로 리다이렉트
 * 
 * 정식 오픈 시 해제 방법:
 * 1. TEMPORARY_ACCESS_CONTROL 상수를 false로 변경
 * 2. 관련 주석 및 코드 블록 제거
 * 3. 사용자 인증 로직을 원래대로 복원
 * 
 * 검색 키워드: "임시 접근 제한", "순차 오픈용", "TEMPORARY_ACCESS_CONTROL"
 */

import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, where, Timestamp, getDoc, doc, runTransaction } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { ko } from 'date-fns/locale';
import { addDays, startOfDay } from 'date-fns';
import { parseISO } from 'date-fns/parseISO';
import { auth } from '../../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import ClientLayout from '../../../components/layout/ClientLayout';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import {
  FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS,
  getFacilityReservationApplicationWindowEnd,
  getFacilityReservationApplicationWindowMessage,
  getFacilityReservationApplicationWindowStart,
  isWithinFacilityReservationApplicationWindow
} from '../../../utils/facilityReservationDate';

// ===== 임시 접근 제한 설정 (순차 오픈용) =====
// TODO: 사이트 정식 오픈 시 이 플래그를 false로 변경하여 접근 제한을 해제하세요
const TEMPORARY_ACCESS_CONTROL = false;
// 허용되는 최대 userLevel (4 이하만 접근 가능, 5 이상은 제한)
const MAX_ALLOWED_USER_LEVEL = 4;
// 추가로 허용되는 특정 userLevel (7도 접근 가능)
const ADDITIONAL_ALLOWED_USER_LEVEL = 7;
// ============================================

const CONTRACTOR_USER_LEVEL = 3; // 공사 관계자

// 한국어 로케일 등록
registerLocale('ko', ko as any);
setDefaultLocale('ko');

	interface Facility {
	  id: string;
	  name: string;
	  available: boolean;
	  basePrice?: number;
	  price: number;
	  notice: string;
	  operatingHours: {
	    start: number;
	    end: number;
	  };
  operatingDays: number[];
  closedPeriods?: Array<{
    id: string;
    start: {
      date: string;
      time: number;
    };
    end: {
      date: string;
      time: number;
    };
  }>;
  imageUrl?: string;
  reservationDays: number;  // 예약 시작 가능일
  reservationWindowDays: number; // 예약 신청 가능 기간
  reservationTimeUnit: number;  // 예약 시간 단위
	reservationType: string;  // 예약 타입
}

const getEffectiveReservationDays = (facility: Facility, userLevel: number | null) => {
  if (userLevel === CONTRACTOR_USER_LEVEL) return 0;
  return facility.reservationDays ?? 0;
};

const getClosedTimeRangesForDate = (facility: Facility, date: Date): Array<[number, number]> => {
  const currentDay = startOfDay(date);
  const currentDayTime = currentDay.getTime();
  const ranges: Array<[number, number]> = [];

  if (!facility.closedPeriods || facility.closedPeriods.length === 0) return ranges;

  for (const period of facility.closedPeriods) {
    const periodStartDay = startOfDay(parseISO(period.start.date));
    const periodEndDay = startOfDay(parseISO(period.end.date));

    const periodStartTime = periodStartDay.getTime();
    const periodEndTime = periodEndDay.getTime();

    if (currentDayTime < periodStartTime || currentDayTime > periodEndTime) continue;

    let rangeStart = 0;
    let rangeEnd = 24;

    if (periodStartTime === periodEndTime) {
      rangeStart = period.start.time;
      rangeEnd = period.end.time;
    } else if (currentDayTime === periodStartTime) {
      rangeStart = period.start.time;
    } else if (currentDayTime === periodEndTime) {
      rangeEnd = period.end.time;
    }

    if (rangeStart < rangeEnd) {
      ranges.push([rangeStart, rangeEnd]);
    }
  }

  return ranges;
};

const getClosedTimeSlotsForDate = (facility: Facility, date: Date) => {
  const { start: operatingStart, end: operatingEnd } = facility.operatingHours;
  const unit = facility.reservationTimeUnit || 1;

  const closedTimes: { [key: number]: boolean } = {};
  const ranges = getClosedTimeRangesForDate(facility, date);

  for (const [rangeStart, rangeEnd] of ranges) {
    const overlapStart = Math.max(rangeStart, operatingStart);
    const overlapEnd = Math.min(rangeEnd, operatingEnd);

    for (let hour = overlapStart; hour < overlapEnd; hour += unit) {
      closedTimes[hour] = true;
    }
  }

  return closedTimes;
};

const hasReservableTimeOnDate = (facility: Facility, date: Date) => {
  const { start: operatingStart, end: operatingEnd } = facility.operatingHours;
  const unit = facility.reservationTimeUnit || 1;

  if (operatingStart >= operatingEnd) return false;

  const closedTimes = getClosedTimeSlotsForDate(facility, date);

  for (let hour = operatingStart; hour < operatingEnd; hour += unit) {
    if (!closedTimes[hour]) return true;
  }

  return false;
};

const FacilityReservationPage: React.FC = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [availableTimes, setAvailableTimes] = useState<number[]>([]);
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [selectedEndTime, setSelectedEndTime] = useState<number | null>(null);
  const [selectedHours, setSelectedHours] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [userLevel, setUserLevel] = useState<number | null>(null);
  const [excludedDates, setExcludedDates] = useState<Date[]>([]);
  const [bookedTimes, setBookedTimes] = useState<{[key: number]: boolean}>({});
  const [closedTimes, setClosedTimes] = useState<{[key: number]: boolean}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSelectingStartTime, setIsSelectingStartTime] = useState(true);
  const navigate = useNavigate();
  const { loading: emailVerificationLoading } = useEmailVerification();

  // 사용자 인증 상태 및 권한 확인
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        navigate('/signin');
        return;
      }

      try {
        const userProfileRef = doc(db, 'user_profiles', authUser.uid);
        const userProfileSnap = await getDoc(userProfileRef);

        if (userProfileSnap.exists()) {
          const userProfileData = userProfileSnap.data();
          const nextUserLevel = userProfileData.userLevel ?? 999; // 기본값: 999 (최고 제한)
          setUserLevel(nextUserLevel);

          // ===== 임시 접근 제한 체크 (순차 오픈용) =====
          if (TEMPORARY_ACCESS_CONTROL) {
            // userLevel이 MAX_ALLOWED_USER_LEVEL보다 크고, ADDITIONAL_ALLOWED_USER_LEVEL도 아닌 경우 접근 제한
            if (nextUserLevel > MAX_ALLOWED_USER_LEVEL && nextUserLevel !== ADDITIONAL_ALLOWED_USER_LEVEL) {
              console.log(`접근 제한: userLevel ${nextUserLevel} (허용: ${MAX_ALLOWED_USER_LEVEL} 이하 또는 ${ADDITIONAL_ALLOWED_USER_LEVEL})`);
              navigate('/site-preparation');
              return;
            }
          }
          // ============================================
        } else {
          setUserLevel(999);

          if (TEMPORARY_ACCESS_CONTROL) {
            // 사용자 프로필이 없는 경우도 접근 제한
            console.log('접근 제한: 사용자 프로필 없음');
            navigate('/site-preparation');
            return;
          }
        }
      } catch (error) {
        console.error('사용자 권한 확인 실패:', error);
        setUserLevel(999);

        if (TEMPORARY_ACCESS_CONTROL) {
          navigate('/site-preparation');
          return;
        }
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const q = query(
          collection(db, 'facilities'),
          where('available', '==', true),
          orderBy('name', 'asc')
        );
        const querySnapshot = await getDocs(q);
        
	        const facilitiesData = querySnapshot.docs.map(doc => {
	          const data = doc.data();
	          return {
	            id: doc.id,
	            name: data.name || '',
	            available: data.available ?? true,
	            basePrice: data.basePrice ?? undefined,
	            price: data.price ?? 0,
	            notice: data.notice || '',
	            operatingHours: data.operatingHours || { start: 9, end: 18 },
	            operatingDays: data.operatingDays || [1, 2, 3, 4, 5],
	            closedPeriods: data.closedPeriods || [],
            imageUrl: data.imageUrl || '',
            reservationDays: data.reservationDays ?? 0,
            reservationWindowDays: data.reservationWindowDays ?? FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS,
            reservationTimeUnit: data.reservationTimeUnit || 1,
            reservationType: data.reservationType || '',
          };
        });

        setFacilities(facilitiesData);
      } catch (error) {
        console.error('시설물 목록 조회 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFacilities();
  }, []);

  useEffect(() => {
    if (selectedFacility && selectedDate) {
      const fetchBookedTimes = async () => {
        try {
          // 선택한 날짜의 시작과 끝 시간 계산
          const startOfSelectedDate = new Date(selectedDate);
          startOfSelectedDate.setHours(0, 0, 0, 0);
          
          const endOfSelectedDate = new Date(selectedDate);
          endOfSelectedDate.setHours(23, 59, 59, 999);
          
          // Firestore 날짜 타입으로 변환
          const startTimestamp = Timestamp.fromDate(startOfSelectedDate);
          const endTimestamp = Timestamp.fromDate(endOfSelectedDate);
          
          // 해당 날짜, 시설물에 대한 예약 조회
          const reservationsRef = collection(db, 'reservations');
          const q = query(
            reservationsRef,
            where('facilityId', '==', selectedFacility.id),
            where('reservationDate', '>=', startTimestamp),
            where('reservationDate', '<=', endTimestamp),
            where('status', 'in', ['pending', 'approved']) // 대기중이거나 승인된 예약만 조회
          );
          
          const querySnapshot = await getDocs(q);
          
          // 이미 예약된 시간대 계산
          const bookedTimeSlots: {[key: number]: boolean} = {};
          
          querySnapshot.forEach(doc => {
            const reservation = doc.data();
            const startTime = reservation.startTime;
            const endTime = reservation.endTime;
            
            // 시작 시간부터 종료 시간까지의 모든 시간대를 예약됨으로 표시
            for (let hour = startTime; hour < endTime; hour += selectedFacility.reservationTimeUnit) {
              bookedTimeSlots[hour] = true;
            }
          });
          
          setBookedTimes(bookedTimeSlots);
        } catch (err) {
          console.error('예약된 시간대 조회 실패:', err);
        }
      };
      
      fetchBookedTimes();
    } else {
      setBookedTimes({});
    }
  }, [selectedFacility, selectedDate]);

  useEffect(() => {
    if (selectedFacility && selectedDate) {
      const generateAvailableTimes = () => {
        const times: number[] = [];
        const { start, end } = selectedFacility.operatingHours;
        const selectedDay = selectedDate.getDay();
        const nextClosedTimes = getClosedTimeSlotsForDate(selectedFacility, selectedDate);
        setClosedTimes(nextClosedTimes);

        // 선택된 날짜가 운영 요일에 포함되어 있는지 확인
        if (selectedFacility.operatingDays.includes(selectedDay)) {
          // reservationTimeUnit에 따라 시간 간격 조정
          for (let hour = start; hour < end; hour += selectedFacility.reservationTimeUnit) {
            // 이미 예약된 시간대/휴무 시간대가 아닌 경우에만 추가
            if (!bookedTimes[hour] && !nextClosedTimes[hour]) {
              times.push(hour);
            }
          }
        }

        setAvailableTimes(times);
        // 시간 선택 상태 초기화
        resetTimeSelection();
      };

      generateAvailableTimes();
    } else {
      setAvailableTimes([]);
      setClosedTimes({});
      resetTimeSelection();
    }
  }, [selectedFacility, selectedDate, bookedTimes]);

  // 선택된 시설물이 변경될 때 실행되는 useEffect
  useEffect(() => {
    if (selectedFacility) {
      const generateExcludedDates = () => {
        const excluded: Date[] = [];
        const today = startOfDay(new Date());
        const reservationStartDate = getFacilityReservationApplicationWindowStart(
          today,
          getEffectiveReservationDays(selectedFacility, userLevel)
        );
        const reservationWindowEnd = getFacilityReservationApplicationWindowEnd(
          today,
          selectedFacility.reservationWindowDays
        );


        // reservationDays 이전의 모든 날짜는 예약 불가
        for (let date = today; date < reservationStartDate; date = addDays(date, 1)) {
          excluded.push(date);
        }

        // reservationDays 이후의 날짜들 중에서 운영 요일이 아니거나 휴무 기간인 날짜 제외
        for (let date = reservationStartDate; date <= reservationWindowEnd; date = addDays(date, 1)) {
          const dayOfWeek = date.getDay();
          
          // 운영 요일에 포함되지 않는 날짜
          if (!selectedFacility.operatingDays.includes(dayOfWeek)) {
            excluded.push(date);
            continue;
          }

          // 휴무로 인해 예약 가능한 시간이 전혀 없는 날짜만 제외
          if (!hasReservableTimeOnDate(selectedFacility, date)) {
            excluded.push(date);
          }
        }

        
        setExcludedDates(excluded);
      };

      generateExcludedDates();
    } else {
      setExcludedDates([]);
    }
  }, [selectedFacility, userLevel]);

  // 수정된 코드: 선택된 시설물과 제외 날짜가 변경될 때마다
  // 현재 선택된 날짜의 유효성을 검사하고 필요한 경우 조정하는 로직
  useEffect(() => {
    if (selectedFacility && selectedDate && excludedDates.length > 0) {
      // 현재 선택된 날짜가 제외 날짜에 포함되어 있는지 확인
      const isSelectedDateExcluded = excludedDates.some(excludedDate => 
        startOfDay(excludedDate).getTime() === startOfDay(selectedDate).getTime()
      );
      
      if (isSelectedDateExcluded) {
        
        // 오늘부터 시설물별 예약 가능 기간 이내의 모든 날짜를 생성
        const today = startOfDay(new Date());
        const reservationWindowEnd = getFacilityReservationApplicationWindowEnd(
          today,
          selectedFacility.reservationWindowDays
        );
        let allDates: Date[] = [];
        
        for (let date = today; date <= reservationWindowEnd; date = addDays(date, 1)) {
          allDates.push(new Date(date));
        }
        
        // 예약 가능한 날짜만 필터링 (excludedDates에 포함되지 않은 날짜)
        const availableDates = allDates.filter(date => 
          !excludedDates.some(excludedDate => 
            startOfDay(excludedDate).getTime() === startOfDay(date).getTime()
          )
        );
        
        if (availableDates.length > 0) {
          // 가장 빠른 예약 가능일 선택
          setSelectedDate(availableDates[0]);
          
          // 사용자에게 알림 메시지 표시
          setError(`선택한 시설물(${selectedFacility.name})에서는 이전에 선택한 날짜에 예약이 불가능합니다. 예약 가능한 날짜로 자동 변경되었습니다.`);
          
          // 3초 후 알림 메시지 숨김
          setTimeout(() => {
            setError(null);
          }, 5000);
        } else {
          // 예약 가능한 날짜가 없는 경우
          setSelectedDate(null);
          setError(`현재 ${selectedFacility.name}에서 예약 가능한 날짜가 없습니다.`);
        }
      }
    }
  }, [selectedFacility, excludedDates, selectedDate]);

  // 시간을 포맷팅하는 헬퍼 함수
  const formatTime = (hour: number) => {
    return `${String(hour).padStart(2, '0')}:00`;
  };

  // 슬라이더 마크를 생성하는 함수 수정
  const generateMarks = () => {
    if (!selectedFacility) return {};
    
    const { start, end } = selectedFacility.operatingHours;
    const marks: { [key: number]: { label: string; style?: React.CSSProperties } } = {};
    
    for (let hour = start; hour <= end; hour += selectedFacility.reservationTimeUnit) {
      marks[hour] = {
        label: formatTime(hour),
        style: bookedTimes[hour]
          ? { color: '#EF4444' } // 예약된 시간은 빨간색으로 표시
          : closedTimes[hour]
            ? { color: '#9CA3AF' } // 휴무 시간은 회색으로 표시
            : undefined
      };
    }
    
    return marks;
  };

  // 슬라이더 값이 변경될 때 호출되는 함수 수정
  const handleTimeRangeChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      const [start, end] = value;
      
      // 선택된 범위 내에 예약/휴무 시간이 있는지 확인
      let hasBlockedTime = false;
      for (let hour = start; hour < end; hour += selectedFacility?.reservationTimeUnit || 1) {
        if (bookedTimes[hour] || closedTimes[hour]) {
          hasBlockedTime = true;
          break;
        }
      }

      // 예약/휴무 시간이 없는 경우에만 값 업데이트
      if (!hasBlockedTime) {
        setSelectedTime(start);
        setSelectedEndTime(end);
      }
    }
  };

  // 예약 가능한 시간 범위를 반환하는 함수 추가
  const getValidTimeRanges = () => {
    if (!selectedFacility) return [];
    
    const { start, end } = selectedFacility.operatingHours;
    const ranges: [number, number][] = [];
    let currentStart: number | null = null;

    for (let hour = start; hour < end; hour += selectedFacility.reservationTimeUnit) {
      if (!bookedTimes[hour] && !closedTimes[hour]) {
        if (currentStart === null) {
          currentStart = hour;
        }
      } else {
        if (currentStart !== null) {
          ranges.push([currentStart, hour]);
          currentStart = null;
        }
      }
    }

    // 마지막 범위 처리
    if (currentStart !== null) {
      ranges.push([currentStart, end]);
    }

    return ranges;
  };

  // 시간 선택 상태 초기화 함수
  const resetTimeSelection = () => {
    setSelectedTime(null);
    setSelectedEndTime(null);
    setSelectedHours([]);
  };

  // 날짜가 변경될 때 시간 선택 상태 초기화
  useEffect(() => {
    resetTimeSelection();
  }, [selectedDate]);

  // 시설물 선택 시 에러 메시지 초기화
  const handleFacilitySelect = (facility: Facility) => {
    setSelectedFacility(facility);
    setError(null);
    
    // 날짜 선택 섹션으로 스크롤
    setTimeout(() => {
      const dateSection = document.getElementById('date-selection-section');
      if (dateSection) {
        dateSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // 날짜 선택 시 에러 메시지 초기화
  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date);
    setError(null);
    // 날짜 선택 시 시간 선택 영역으로 스크롤
    setTimeout(() => {
      const timeSection = document.getElementById('time-selection-section');
      if (timeSection) {
        timeSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // 시간 선택 시 에러 메시지 초기화
  const handleTimeSelect = (hour: number) => {
    const isBooked = bookedTimes[hour];
    const isClosed = closedTimes[hour];
    const isSelected = selectedHours.includes(hour);
    const isSelectable = !isBooked && !isClosed;
    
    if (!isSelectable) return;
    
    let newSelectedHours: number[];
    
    if (isSelected) {
      // 선택 해제
      newSelectedHours = selectedHours.filter(h => h !== hour);
      
      // 시작 또는 종료 시간을 취소한 경우, 예약된 시간대를 제외한 새로운 범위 계산
      if (newSelectedHours.length > 0) {
        const minHour = Math.min(...newSelectedHours);
        const maxHour = Math.max(...newSelectedHours);
        
        // 예약된 시간대를 제외한 새로운 범위 계산
        const continuousHours: number[] = [];
        for (let h = minHour; h <= maxHour; h++) {
          if (!bookedTimes[h] && !closedTimes[h]) {
            continuousHours.push(h);
          } else {
            // 예약된 시간대를 만나면 범위를 나눔
            if (continuousHours.length > 0) {
              break;
            }
          }
        }
        
        // 연속된 시간이 있다면 해당 범위만 선택
        if (continuousHours.length > 0) {
          newSelectedHours = continuousHours;
        }
      }
    } else if (selectedHours.length === 0) {
      // 첫 번째 선택
      newSelectedHours = [hour];
    } else {
      // 연속된 시간 선택
      const minHour = Math.min(...selectedHours);
      const maxHour = Math.max(...selectedHours);
      
      if (hour < minHour) {
        // 선택된 시간보다 이전 시간 선택
        newSelectedHours = Array.from(
          { length: maxHour - hour + 1 },
          (_, i) => hour + i
        );
      } else if (hour > maxHour) {
        // 선택된 시간보다 이후 시간 선택
        newSelectedHours = Array.from(
          { length: hour - minHour + 1 },
          (_, i) => minHour + i
        );
      } else {
        // 선택된 시간 사이의 시간을 클릭한 경우 아무 변화 없음
        return;
      }

      // 연속되지 않은 시간을 선택한 경우 전체 취소
      const isContinuous = newSelectedHours.every((h, i) => {
        if (i === 0) return true;
        return h === newSelectedHours[i - 1] + 1;
      });

      if (!isContinuous) {
        newSelectedHours = [hour];
      }
    }

    const hasBlocked = newSelectedHours.some(h => bookedTimes[h] || closedTimes[h]);
    if (hasBlocked) {
      setError('선택한 범위에 이미 예약되었거나 휴무인 시간이 포함되어 있습니다. 다른 시간을 선택해주세요.');
      setSelectedHours([hour]);
      setSelectedTime(hour);
      setSelectedEndTime(hour + 1);
      return;
    }
    
    setSelectedHours(newSelectedHours);
    if (newSelectedHours.length > 0) {
      setSelectedTime(Math.min(...newSelectedHours));
      setSelectedEndTime(Math.max(...newSelectedHours) + 1);
    } else {
      setSelectedTime(null);
      setSelectedEndTime(null);
    }
    setError(null);
  };

  const handleReservation = async () => {
    if (!selectedFacility || !selectedDate || isSubmitting) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/signin');
        return;
      }
      
      // user_profiles에서 사용자 정보 가져오기
      const userProfileRef = doc(db, 'user_profiles', currentUser.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (!userProfileSnap.exists()) {
        throw new Error('사용자 프로필 정보를 찾을 수 없습니다.');
      }
      
      const userProfileData = userProfileSnap.data();
      
      // 필수 정보 확인
      if (!userProfileData.name) {
        throw new Error('사용자 이름 정보가 없습니다.');
      }
      
      if (!userProfileData.email && !currentUser.email) {
        throw new Error('이메일 정보가 없습니다.');
      }
      
      const startTime = selectedTime || 0;
      const endTime = selectedFacility.reservationType === 'time-slot' 
        ? (selectedTime || 0) + selectedFacility.reservationTimeUnit
        : selectedEndTime || 0;

      const effectiveReservationDays = getEffectiveReservationDays(selectedFacility, userLevel);
      if (!isWithinFacilityReservationApplicationWindow(
        selectedDate,
        new Date(),
        effectiveReservationDays,
        selectedFacility.reservationWindowDays
      )) {
        throw new Error(getFacilityReservationApplicationWindowMessage(selectedFacility.reservationWindowDays));
      }

      // 휴무 시간대 포함 여부 확인
      const closedRanges = getClosedTimeRangesForDate(selectedFacility, selectedDate);
      const overlapsClosed = closedRanges.some(([closedStart, closedEnd]) => startTime < closedEnd && endTime > closedStart);
      if (overlapsClosed) {
        throw new Error('선택한 시간대에 휴무 시간이 포함되어 있습니다. 다른 시간을 선택해주세요.');
      }
      
      // 선택한 날짜의 시작과 끝 시간 계산
      const reservationDate = new Date(selectedDate);
      reservationDate.setHours(0, 0, 0, 0);
      
      // 예약 정보 준비 (Firestore에 저장하지는 않음)
      const reservationData = {
        facilityId: selectedFacility.id,
        facilityName: selectedFacility.name,
        reservationDate,
        startTime,
        endTime,
        reservationType: selectedFacility.reservationType,
        uid: currentUser.uid,
        name: userProfileData.name,
        email: userProfileData.email || currentUser.email || '',
        phoneNumber: userProfileData.phoneNumber || '',
        organization: userProfileData.organization || '',
        status: 'pending',
        createdAt: new Date()
      };
      
      // 겹치는 예약이 있는지만 확인
      const reservationsRef = collection(db, 'reservations');
      const startTimestamp = Timestamp.fromDate(reservationDate);
      
      const endOfReservationDate = new Date(reservationDate);
      endOfReservationDate.setHours(23, 59, 59, 999);
      const endTimestamp = Timestamp.fromDate(endOfReservationDate);
      
      const q = query(
        reservationsRef,
        where('facilityId', '==', selectedFacility.id),
        where('reservationDate', '>=', startTimestamp),
        where('reservationDate', '<=', endTimestamp),
        where('status', 'in', ['pending', 'approved'])
      );
      
      const querySnapshot = await getDocs(q);
      
      // 겹치는 예약 확인
      const overlapping = querySnapshot.docs.some(doc => {
        const existingReservation = doc.data();
        const existingStart = existingReservation.startTime;
        const existingEnd = existingReservation.endTime;
        
        // 시간대 겹침 여부 확인
        const isOverlapping = (
          (startTime < existingEnd && endTime > existingStart) || // 시간대 겹침
          (startTime === existingStart && endTime === existingEnd) // 동일 시간
        );
        
        return isOverlapping;
      });
      
      if (overlapping) {
        throw new Error('이미 예약된 시간대입니다. 다른 시간을 선택해주세요.');
      }
      
      // 문서 생성 없이 데이터만 확인 페이지로 전달
      navigate('/facility/confirm', { state: { reservationData } });
    } catch (err: any) {
      console.error('예약 확인 실패:', err);
      setError(err.message || '예약 처리 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || emailVerificationLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        {/* 본문 컨테이너 - 반응형 중앙 정렬 */}
        <div className="max-w-[760px] mx-auto relative pt-8 px-4 sm:px-0">
          {/* 헤더 섹션 */}
          <div className="mt-8">
            <div className="text-slate-800 text-3xl sm:text-4xl font-extrabold font-['Pretendard'] leading-9">
              시설물 예약
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-slate-700 text-lg sm:text-xl font-normal font-['Pretendard'] leading-relaxed sm:leading-9">
                원하는 시설물과 날짜를 선택해주세요
              </div>
              <button
                onClick={() => navigate('/facility/reservations')}
                className="w-full sm:w-44 h-12 sm:h-16 bg-blue-500 hover:bg-blue-600 rounded-sm flex-shrink-0"
              >
                <div className="text-center text-white text-base sm:text-lg font-bold font-['Pretendard'] leading-relaxed sm:leading-9">
                  예약 내역
                </div>
              </button>
            </div>
          </div>

          {/* 구분선 */}
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

          {error && (
            <div className="mt-8 bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-8 space-y-8">
            {/* 시설물 선택 섹션 */}
            <div>
              <div className="text-slate-800 text-lg sm:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                시설물 선택
              </div>
              <div className="bg-white p-4 sm:p-8 rounded border border-black/5">
                <div className="grid grid-cols-1 gap-4">
                  {facilities.map((facility) => (
                    <div
                      key={facility.id}
                      className={`p-4 sm:p-6 rounded-sm outline outline-[0.50px] cursor-pointer transition-colors ${
                        selectedFacility?.id === facility.id
                          ? 'outline-blue-500 bg-blue-50'
                          : 'outline-gray-300 hover:outline-blue-300'
                      }`}
                      onClick={() => handleFacilitySelect(facility)}
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0">
	                          <h3 className="text-slate-800 text-base sm:text-lg font-bold font-['Pretendard'] mb-2">
	                            {facility.name}
	                          </h3>
	                          <p className="text-slate-700 text-sm sm:text-base font-normal font-['Pretendard']">
	                            {(facility.basePrice ?? facility.price).toLocaleString()}원
	                          </p>
	                        </div>
	                        {facility.imageUrl && (
	                          <img
	                            src={facility.imageUrl}
                            alt={facility.name}
                            className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-sm flex-shrink-0"
                          />
                        )}
                      </div>
                      {facility.notice && (
                        <p className="mt-4 text-slate-600 text-xs sm:text-sm font-normal font-['Pretendard'] break-words">
                          {facility.notice}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 날짜 및 시간 선택 섹션 */}
            <div id="date-selection-section">
              <div className="text-slate-800 text-lg sm:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                날짜 및 시간 선택
              </div>
              <div className="bg-white p-4 sm:p-8 rounded border border-black/5">
                <div className="mb-8">
                  <div className="text-slate-800 text-base font-semibold font-['Pretendard'] mb-4">
                    날짜 선택
                  </div>
                  <div className="relative">
	                    {selectedFacility ? (
	                      <DatePicker
	                        selected={selectedDate}
	                        onChange={handleDateSelect}
	                        dateFormat="yyyy년 MM월 dd일"
	                        minDate={getFacilityReservationApplicationWindowStart(new Date(), getEffectiveReservationDays(selectedFacility, userLevel))}
	                        maxDate={getFacilityReservationApplicationWindowEnd(new Date(), selectedFacility.reservationWindowDays)}
	                        locale="ko"
	                        className="w-full"
	                        placeholderText="날짜를 선택해주세요"
	                        showTimeSelect={false}
	                        showPopperArrow={false}
	                        popperClassName="react-datepicker-popper"
	                        popperPlacement="bottom-start"
	                        inline
	                        openToDate={selectedDate || getFacilityReservationApplicationWindowStart(new Date(), getEffectiveReservationDays(selectedFacility, userLevel))}
	                        calendarClassName="!bg-white !rounded-none !border-0 !shadow-none custom-datepicker-calendar w-full max-w-full sm:max-w-[700px]"
	                        excludeDates={excludedDates}
	                        dayClassName={date => {
	                          const isToday = new Date().toDateString() === date.toDateString();
	                          const isCurrentMonth = date.getMonth() === (selectedDate ? selectedDate.getMonth() : new Date().getMonth());
                          const isDisabled = excludedDates.some(d => d.toDateString() === date.toDateString());
                          let base = 'text-base font-medium text-center';
                          if (!isCurrentMonth) base += ' text-gray-300';
                          else if (isToday) base += ' text-green-600 font-bold';
                          else if (isDisabled) base += ' text-gray-300';
                          else base += ' text-gray-800';
                          return base;
                        }}
                        renderDayContents={(day, date) => {
                          const isToday = new Date().toDateString() === date.toDateString();
                          return (
                            <div className="flex flex-col items-center justify-center">
                              <span>{day}</span>
                              {isToday && <span className="today-label text-xs text-green-600">오늘</span>}
                            </div>
                          );
                        }}
                        renderCustomHeader={({
                          date,
                          decreaseMonth,
                          increaseMonth,
                          prevMonthButtonDisabled,
                          nextMonthButtonDisabled,
                        }) => (
                          <div className="flex items-center justify-between px-4 py-2 mb-2">
                            <button
                              onClick={decreaseMonth}
                              disabled={prevMonthButtonDisabled}
                              className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>
                            <div className="text-slate-900 text-lg sm:text-2xl font-extrabold tracking-tight text-center" style={{letterSpacing: '-0.02em'}}>
                              {date.getFullYear()}.{String(date.getMonth() + 1).padStart(2, '0')}
                            </div>
                            <button
                              onClick={increaseMonth}
                              disabled={nextMonthButtonDisabled}
                              className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        )}
                      />
                    ) : (
                      <div className="h-[200px] sm:h-[300px] flex items-center justify-center bg-white rounded-sm outline outline-[0.50px] outline-gray-300">
                        <p className="text-slate-600 text-sm sm:text-base font-normal font-['Pretendard'] text-center px-4">
                          시설물을 먼저 선택해주세요
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedFacility && selectedDate && availableTimes.length > 0 && (
                  <div id="time-selection-section">
                    <div className="flex justify-between items-center mb-4">
                      <div className="text-slate-800 text-base font-semibold font-['Pretendard']">
                        시간 선택
                      </div>
                      {selectedFacility.reservationType === 'start-end' && selectedTime !== null && selectedEndTime !== null && (
                        <span className="text-blue-600 text-base font-medium font-['Pretendard']">
                          {formatTime(selectedTime)} - {formatTime(selectedEndTime)}
                        </span>
                      )}
                    </div>

                    {selectedFacility.reservationType === 'time-slot' ? (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {availableTimes.map((hour) => (
                          <button
                            key={hour}
                            className={`h-10 sm:h-12 rounded-sm outline outline-[0.50px] ${
                              selectedTime === hour
                                ? 'bg-blue-500 text-white outline-blue-500'
                                : 'bg-white outline-gray-300 hover:outline-blue-300'
                            }`}
                            onClick={() => setSelectedTime(hour)}
                          >
                            <div className="text-center text-sm sm:text-base font-medium font-['Pretendard']">
                              {formatTime(hour)}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="text-slate-700 text-sm sm:text-base font-normal font-['Pretendard']">
                            예약할 시간을 선택해주세요
                          </div>
                          {selectedHours.length > 0 && (
                            <button
                              onClick={resetTimeSelection}
                              className="text-blue-600 text-sm font-medium font-['Pretendard'] hover:text-blue-800"
                            >
                              다시 선택
                            </button>
                          )}
                        </div>
		                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
		                          {Array.from({ length: Math.max(0, selectedFacility.operatingHours.end - selectedFacility.operatingHours.start) }, (_, i) => {
		                            const hour = selectedFacility.operatingHours.start + i;
		                            const isBooked = bookedTimes[hour];
		                            const isClosed = closedTimes[hour];
		                            const isSelected = selectedHours.includes(hour);
		                            const isSelectable = !isBooked && !isClosed;
	                            
	                            return (
	                              <button
	                                key={hour}
	                                className={`
	                                  h-10 sm:h-12 rounded-sm outline outline-[0.50px] relative
	                                  ${isBooked ? 'bg-red-50 text-red-500 outline-red-300 cursor-not-allowed' :
	                                    isClosed ? 'bg-gray-100 text-gray-400 outline-gray-200 cursor-not-allowed' :
	                                    isSelected ? 'bg-blue-500 text-white outline-blue-500' : 
	                                    isSelectable ? 'bg-white outline-gray-300 hover:outline-blue-300' : 
	                                    'bg-gray-50 text-gray-400 outline-gray-200 cursor-not-allowed'}
	                                `}
	                                onClick={() => handleTimeSelect(hour)}
	                                disabled={!isSelectable || (selectedHours.length > 0 && hour > Math.min(...selectedHours) && hour < Math.max(...selectedHours))}
	                              >
	                                <div className="text-center text-sm sm:text-base font-medium font-['Pretendard']">
	                                  {formatTime(hour)}
	                                </div>
                                {isSelected && (hour === Math.min(...selectedHours) || hour === Math.max(...selectedHours)) && (
                                  <div className={`absolute ${hour === Math.min(...selectedHours) ? '-top-1' : '-bottom-1'} left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-500 rounded-full`}></div>
                                )}
                              </button>
                            );
                          })}
	                        </div>
	                        <div className="flex items-center justify-center mt-4">
	                          <span className="inline-block w-3 h-3 bg-red-500 rounded-full mr-2"></span>
	                          <span className="text-slate-600 text-xs sm:text-sm font-normal font-['Pretendard']">
	                            빨간색으로 표시된 시간은 이미 예약된 시간대입니다.
	                          </span>
	                        </div>
	                        <div className="flex items-center justify-center mt-2">
	                          <span className="inline-block w-3 h-3 bg-gray-400 rounded-full mr-2"></span>
	                          <span className="text-slate-600 text-xs sm:text-sm font-normal font-['Pretendard']">
	                            회색으로 표시된 시간은 휴무 시간대입니다.
	                          </span>
	                        </div>
	                      </div>
	                    )}
	                  </div>
	                )}

                {selectedFacility && selectedDate && availableTimes.length === 0 && (
                  <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                    <p className="text-yellow-700 text-sm sm:text-base">
                      선택하신 날짜는 예약이 불가능한 날짜입니다.
                    </p>
                  </div>
                )}

                {selectedFacility && selectedDate && availableTimes.length > 0 && (
                  <div className="mt-8">
                    <button
                      onClick={handleReservation}
                      disabled={
                        isSubmitting ||
                        (selectedFacility.reservationType === 'time-slot' && selectedTime === null) ||
                        (selectedFacility.reservationType === 'start-end' && (selectedTime === null || selectedEndTime === null))
                      }
                      className={`w-full h-12 sm:h-16 rounded-sm ${
                        isSubmitting ? 'bg-gray-400 cursor-not-allowed' :
                        ((selectedFacility.reservationType === 'time-slot' && selectedTime !== null) ||
                        (selectedFacility.reservationType === 'start-end' && selectedTime !== null && selectedEndTime !== null))
                          ? 'bg-blue-500 hover:bg-blue-600'
                          : 'bg-gray-300 cursor-not-allowed'
                      }`}
                    >
                      <div className="text-center text-white text-base sm:text-lg font-bold font-['Pretendard'] leading-relaxed sm:leading-9">
                        {isSubmitting ? '예약 진행 중...' : '예약하기'}
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 하단 여백 */}
          <div className="h-32"></div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default FacilityReservationPage;

// 아래 CSS를 src/index.css 또는 글로벌 CSS 파일에 추가하세요.
// .custom-datepicker-calendar .react-datepicker__month-container {
//   width: 150% !important;
//   min-width: 480px;
//   max-width: 700px;
// }
// .custom-datepicker-calendar .react-datepicker__day,
// .custom-datepicker-calendar .react-datepicker__day-name {
//   width: 48px;
//   height: 48px;
//   line-height: 48px;
//   font-size: 18px;
// } 
